/**
 * Main Application Component
 *
 * Handles routing and authentication state.
 * Protected routes require authentication.
 */

import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

import { useAuth } from './hooks/useAuth'
import { useAuthStore } from './stores/authStore'
import { MainLayout } from './components/layout/MainLayout'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { BillsPage } from './pages/bills/BillsPage'
import { NewBillPage } from './pages/bills/NewBillPage'
import { BillDetailPage } from './pages/bills/BillDetailPage'
import { BillDepositPage } from './pages/bills/BillDepositPage'
import { BillDepositDetailPage } from './pages/bills/BillDepositDetailPage'
import { ArticlesPage, ArticleDetailPage, ArticleFormPage, ArticleCabysPage, ArticleInventoryLabelsPage, ArticleLabelsPage } from './pages/articles'
import { CreateCreditNotePage } from './pages/bills/CreateCreditNotePage'
import { CreateDebitNotePage } from './pages/bills/CreateDebitNotePage'
import { EditLinePage } from './pages/bills/EditLinePage'
import { ClientsPage, ClientDetailPage, ClientFormPage, ClientDepositPage, ClientDepositDetailPage } from './pages/clients'
import { CashboxPage, CashboxOpenPage, CashboxClosePage, CashboxHistoryPage, CashboxDetailPage } from './pages/cashbox'
import {
  ReportsIndexPage,
  SalesSummaryReport,
  TopArticlesReport,
  ArticleCostsReport,
  ArticlePricesReport,
  DetailedSalesReport,
  AccountsReceivableReport,
  ZeroStockReport,
  InventoryValueReport,
  AccountsPayableReport,
  DepositsHistoryReport,
} from './pages/reports'
import { SettingsPage } from './pages/SettingsPage'
import { InvoicingHistoryPage, InvoicingIncidentsPage, InvoicingIncidentDetailPage, AlanubeRegistrationPage, RenewCertificatePage } from './pages/settings'
import { BillReceiptPage, DepositReceiptPage, ClientDepositReceiptPage } from './pages/receipts'
import { PurchasesPage, ReceivedDocumentsPage, PurchaseDetailPage } from './pages/purchases'
import { ProformasPage, ProformaDetailPage } from './pages/proformas'
import { UsersPage, UserDetailPage, UserFormPage } from './pages/users'
import {
  ProvidersPage,
  ProviderDetailPage,
  ProviderFormPage,
  FamiliesPage,
  FamilyDetailPage,
  FamilyFormPage,
  GroupsPage,
  GroupDetailPage,
  GroupFormPage,
  LocationsPage,
  LocationDetailPage,
  LocationFormPage,
} from './pages/catalogs'

/**
 * Protected Route Wrapper
 *
 * Redirects to login if user is not authenticated.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const isInitialized = useAuthStore((state) => state.isInitialized)

  // Show loading while checking auth
  if (!isInitialized || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

/**
 * Main App Component
 */
export default function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth)

  // Check auth status once on app mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="bills" element={<BillsPage />} />
        <Route path="bills/new" element={<NewBillPage />} />
        <Route path="bills/:id" element={<BillDetailPage />} />
        <Route path="bills/:id/deposit" element={<BillDepositPage />} />
        <Route path="bills/:id/deposits/:depositId" element={<BillDepositDetailPage />} />
        <Route path="bills/:id/credit-note" element={<CreateCreditNotePage />} />
        <Route path="bills/:id/debit-note" element={<CreateDebitNotePage />} />
        <Route path="bills/:id/edit-line/:lineId" element={<EditLinePage />} />
        <Route path="articles" element={<ArticlesPage />} />
        <Route path="articles/new" element={<ArticleFormPage />} />
        <Route path="articles/labels" element={<ArticleLabelsPage />} />
        <Route path="articles/labels/inventory" element={<ArticleInventoryLabelsPage />} />
        <Route path="articles/:id" element={<ArticleDetailPage />} />
        <Route path="articles/:id/edit" element={<ArticleFormPage />} />
        <Route path="articles/:id/cabys" element={<ArticleCabysPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/new" element={<ClientFormPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="clients/:id/edit" element={<ClientFormPage />} />
        <Route path="clients/:id/deposit" element={<ClientDepositPage />} />
        <Route path="clients/:id/deposits/:depositId" element={<ClientDepositDetailPage />} />
        <Route path="cashbox" element={<CashboxPage />} />
        <Route path="cashbox/open" element={<CashboxOpenPage />} />
        <Route path="cashbox/close" element={<CashboxClosePage />} />
        <Route path="cashbox/history" element={<CashboxHistoryPage />} />
        <Route path="cashbox/history/:id" element={<CashboxDetailPage />} />
        {/* Reports */}
        <Route path="reports" element={<ReportsIndexPage />} />
        <Route path="reports/sales-summary" element={<SalesSummaryReport />} />
        <Route path="reports/top-articles" element={<TopArticlesReport />} />
        <Route path="reports/article-costs" element={<ArticleCostsReport />} />
        <Route path="reports/article-prices" element={<ArticlePricesReport />} />
        <Route path="reports/detailed-sales" element={<DetailedSalesReport />} />
        <Route path="reports/accounts-receivable" element={<AccountsReceivableReport />} />
        <Route path="reports/zero-stock" element={<ZeroStockReport />} />
        <Route path="reports/inventory-value" element={<InventoryValueReport />} />
        <Route path="reports/accounts-payable" element={<AccountsPayableReport />} />
        <Route path="reports/deposits-history" element={<DepositsHistoryReport />} />
        <Route path="purchases" element={<PurchasesPage />} />
        <Route path="purchases/received" element={<ReceivedDocumentsPage />} />
        <Route path="purchases/new" element={<ReceivedDocumentsPage />} />
        <Route path="purchases/:id" element={<PurchaseDetailPage />} />
        <Route path="proformas" element={<ProformasPage />} />
        <Route path="proformas/:id" element={<ProformaDetailPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/invoicing-history" element={<InvoicingHistoryPage />} />
        <Route path="settings/invoicing-incidents" element={<InvoicingIncidentsPage />} />
        <Route path="settings/invoicing-incidents/:id" element={<InvoicingIncidentDetailPage />} />
        <Route path="settings/hacienda-registration" element={<AlanubeRegistrationPage />} />
        <Route path="settings/renew-certificate" element={<RenewCertificatePage />} />
        {/* Users */}
        <Route path="users" element={<UsersPage />} />
        <Route path="users/new" element={<UserFormPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="users/:id/edit" element={<UserFormPage />} />
        {/* Providers */}
        <Route path="providers" element={<ProvidersPage />} />
        <Route path="providers/new" element={<ProviderFormPage />} />
        <Route path="providers/:id" element={<ProviderDetailPage />} />
        <Route path="providers/:id/edit" element={<ProviderFormPage />} />
        {/* Families */}
        <Route path="families" element={<FamiliesPage />} />
        <Route path="families/new" element={<FamilyFormPage />} />
        <Route path="families/:id" element={<FamilyDetailPage />} />
        <Route path="families/:id/edit" element={<FamilyFormPage />} />
        {/* Groups/Lines */}
        <Route path="groups" element={<GroupsPage />} />
        <Route path="groups/new" element={<GroupFormPage />} />
        <Route path="groups/:id" element={<GroupDetailPage />} />
        <Route path="groups/:id/edit" element={<GroupFormPage />} />
        {/* Locations */}
        <Route path="locations" element={<LocationsPage />} />
        <Route path="locations/new" element={<LocationFormPage />} />
        <Route path="locations/:id" element={<LocationDetailPage />} />
        <Route path="locations/:id/edit" element={<LocationFormPage />} />
      </Route>

      {/* Receipt routes (standalone, for printing) */}
      <Route
        path="/receipts/bill/:billId"
        element={
          <ProtectedRoute>
            <BillReceiptPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/receipts/deposit/:billId/:depositId"
        element={
          <ProtectedRoute>
            <DepositReceiptPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/receipts/client-deposit/:clientId/:depositId"
        element={
          <ProtectedRoute>
            <ClientDepositReceiptPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
